import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import { Gutter, Headline, Loader, Page } from '@gaiads/telia-react-component-library';
import { CheckoutDelivery as CheckoutDeliveryReusable, CheckoutDeliveryTranslations } from '@cdaas/reusable-components-daas';
import { DeliveryDeliveryOption, DeliveryResponse, ProductOrder } from '@cdaas/api-clients/dist/order-openapi';
import { Address } from '@cdaas/api-clients/dist/asset-user-openapi';

import { RootState } from '../../../../store/reducers';
import { refreshUserInfo, shopSetOrderResp, showGrowlNotification } from '../../../../store/actions';
import {
  DeliveryMethodOfOrder,
  DeliveryOptionsRequestBody,
  LocalBundleEnvelopeWithChosenMainVariant,
  NotificationActions,
  OrderRequestBody,
} from '../../../../types';
import { createNewOrder, getDeliveryOptions as getDeliveryOptionsApi, isSelectedBundleAvailable, setStorageItems } from '../../../../utils';

import CheckoutProductCart from '../CheckoutProductCart/CheckoutProductCart';

interface CheckoutDeliveryProps {
  isMobile?: boolean;
}

const CheckoutDelivery: FunctionComponent<CheckoutDeliveryProps> = ({ isMobile }) => {
  const history = useHistory();

  const delivery = useSelector((state: RootState) => state.shop.delivery);
  const product = useSelector((state: RootState) => state.shop.cart);
  const orderingForOrganization = useSelector((state: RootState) => state.shop.orderForOrganization);
  const orderingOnBehalfUserData = useSelector((state: RootState) => state.shop.orderOnBehalfUser);

  const [deliveryData, setDeliveryData] = useState<DeliveryResponse>();
  const [loading, setLoading] = useState<boolean>(true);
  const [buyButtonLoadingSpinner, setBuyButtonLoadingSpinner] = useState<boolean>(false);

  const dispatch = useDispatch();
  const showErrorNotification = (msg: string): NotificationActions => dispatch(showGrowlNotification(msg, 'warning'));

  const { t, i18n } = useTranslation('shop');

  useEffect(() => {
    const getDeliveryOptions = async (product: LocalBundleEnvelopeWithChosenMainVariant, deliveryAddress: Address, isHomeDelivery: boolean) => {
      setLoading(true);
      const reqBody: DeliveryOptionsRequestBody = {
        bundle_id: product.bundle.id,
        zip: deliveryAddress.zip,
        country: deliveryAddress.country,
        is_home: isHomeDelivery,
        main_item_variant_id: product.chosenMainItemVariantId,
        variant_ids: product.bundle.items.map(addon => ({
          bundle_item_id: addon.id,
          variant_id: addon.options?.[0].id ?? '',
          product_id: addon.options?.[0].product_id ?? 0,
        })),
      };

      const deliveryData = await getDeliveryOptionsApi(reqBody);
      if (deliveryData) {
        setDeliveryData(deliveryData);
      } else {
        showErrorNotification(t('checkout.deliveryPage.deliveryMethodsRequestFailed'));
      }
      setLoading(false);
    };
    if (product && delivery.deliveryAddress) {
      void getDeliveryOptions(product, delivery.deliveryAddress, delivery.isHomeDelivery);
    }
    // showErrorNotification and t are not needed dependencies for this useEffect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, delivery.deliveryAddress, delivery.isHomeDelivery]);

  const handleOrder = async (selectedDelivery: DeliveryDeliveryOption) => {
    if (!product || !delivery.deliveryAddress || !delivery.deliveryContact) {
      showErrorNotification(t('checkout.deliveryPage.orderRequestFailed'));
      return;
    }

    setBuyButtonLoadingSpinner(true);

    const isOnBehalfOrder = orderingForOrganization || orderingOnBehalfUserData.status;

    const reqBody: OrderRequestBody = {
      bundle_id: product.bundle.id,
      quantity: 1,
      delivery_method: selectedDelivery.delivery_method_code,
      ship_to: {
        address: delivery.deliveryAddress,
        person: delivery.deliveryContact,
      },
      main_item_variant_id: product.chosenMainItemVariantId,
      is_home_delivery: delivery.isHomeDelivery,
      variant_ids: product.bundle.items.map(addon => ({
        bundle_item_id: addon.id,
        variant_id: addon.options?.[0].id ?? '',
        product_id: addon.options?.[0].product_id ?? 0,
      })),
      is_oob_order: isOnBehalfOrder,
      oob_user_id: orderingForOrganization
        ? null
        : orderingOnBehalfUserData.status && orderingOnBehalfUserData.userToOrderFor?.id
          ? orderingOnBehalfUserData.userToOrderFor.id
          : null,
      location_reference: delivery.locationReference || null,
      call_off_number: delivery.invoicingAccountNumber || null,
    };

    const order: ProductOrder | null = await createNewOrder(reqBody);

    if (order) {
      const selectedDeliveryOption = _.omit(selectedDelivery, 'checked');

      const storeSelectedDeliveryOption = {
        ...selectedDeliveryOption,
        delivery_times_valid_until: deliveryData?.delivery_times_valid_until ?? '',
      } as DeliveryMethodOfOrder;

      dispatch(shopSetOrderResp(order, storeSelectedDeliveryOption));
      // Refresh user's info so if user had single device policy enabled before making this order now it should be in effect
      if (!isOnBehalfOrder) {
        await dispatch(refreshUserInfo());
      }
      setStorageItems(['tOrderResp', 'tSelectedDelivery'], [order, storeSelectedDeliveryOption], 'session');
      history.push('/shop/checkout/summary');
    } else {
      showErrorNotification(t('checkout.deliveryPage.orderRequestFailed'));
      setBuyButtonLoadingSpinner(false);
    }
  };

  const handleGoBack = (): void => {
    history.goBack();
  };

  const translations: CheckoutDeliveryTranslations = {
    deliveryEstimateNotAvailable: t('checkout.deliveryPage.deliveryEstimateNotAvailable'),
    optionDelivered: t('checkout.deliveryPage.optionDelivered'),
    optionDescription: t('checkout.deliveryPage.optionDescription'),
    title: t('checkout.deliveryPage.title'),
    deliveryText: t('checkout.deliveryPage.deliveryText'),
    deliveryComment: t(orderingForOrganization ? 'checkout.deliveryPage.deliveryCommentOrganization' : 'checkout.deliveryPage.deliveryCommentUser'),
    confirmOrderCheckboxLabel: t('checkout.deliveryPage.confirmOrderCheckboxLabel'),
    orderButton: t('checkout.deliveryPage.orderButton'),
    cancelButton: t('checkout.deliveryPage.cancelButton'),
    outOfStockDeliveryComment: t('checkout.deliveryPage.outOfStockDeliveryNotice'),
  };

  if (loading) {
    return (
      <Page>
        <Page.Row alignTextToCenter margin={{ vertical: 'xxlg' }}>
          <Loader />
        </Page.Row>
      </Page>
    );
  }

  return (
    <>
      <Headline size={'md'}>{t('checkout.deliveryPage.headline')}</Headline>
      <Gutter />
      {!!deliveryData && !!delivery?.deliveryAddress && (
        <CheckoutDeliveryReusable
          translations={translations}
          handleNextClick={handleOrder}
          handleBackClick={handleGoBack}
          deliveryOptionsData={deliveryData}
          i18Language={i18n.language}
          selectedAddress={delivery.deliveryAddress}
          nextButtonClickedSpinner={buyButtonLoadingSpinner}
          outOfStockDelivery={!isSelectedBundleAvailable(product, product?.chosenMainItemVariantId)}
          ProductCart={isMobile ? <CheckoutProductCart isMobile /> : undefined}
        />
      )}
    </>
  );
};

export default CheckoutDelivery;
